import { useRef, useState } from 'react';
import AppHelper from '../utils/helper';


interface UseDateInputHandlersProps {
  value: string;
  onChange: (value: string) => void;
}

export const useDateInputHandlers = ({
  value,
  onChange,
}: UseDateInputHandlersProps) => {
  const nativeDateInputRef = useRef<HTMLInputElement | null>(null);

  const [dateInputValue, setDateInputValue] = useState(
    AppHelper.formatDateToMask(value)
  );
  const [isFocused, setIsFocused] = useState(false);

  const applyDateMask = (rawValue: string): string => {
    const numbers = rawValue.replace(/\D/g, '');
    let result = '';

    for (let i = 0; i < numbers.length && i < 8; i++) {
      if (i === 2 || i === 4) result += '.';
      result += numbers[i];
    }

    return result;
  };

  const parseMaskedDate = (maskedValue: string): string => {
    const numbers = maskedValue.replace(/\D/g, '');
    if (numbers.length !== 8) return '';

    const day = Number(numbers.slice(0, 2));
    const month = Number(numbers.slice(2, 4));
    const year = Number(numbers.slice(4, 8));

    const date = new Date(year, month - 1, day);

    if (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    ) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    return '';
  };

  const handleMaskedInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const masked = applyDateMask(e.target.value);
    setDateInputValue(masked);

    const parsed = parseMaskedDate(masked);
    onChange(parsed);
  };

  const handleNativeDateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    onChange(value);

    setDateInputValue(
      value ? AppHelper.formatDateToMask(value) : ''
    );
  };

  const handleCalendarIconClick = (): void => {
    nativeDateInputRef.current?.showPicker?.();
    nativeDateInputRef.current?.focus();
  };

  const handleFocus = (): void => {
    setIsFocused(true);

    if (value && !dateInputValue) {
      setDateInputValue(AppHelper.formatDateToMask(value));
    }
  };

  const handleBlur = (): void => {
    setIsFocused(false);

    if (dateInputValue.replace(/\D/g, '').length !== 8) {
      setDateInputValue('');
      onChange('');
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
    ];

    if (allowedKeys.includes(e.key)) return;

    if (
      (e.ctrlKey || e.metaKey) &&
      ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())
    ) {
      return;
    }

    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  return {
    nativeDateInputRef,
    dateInputValue,
    isFocused,

    handleCalendarIconClick,
    handleNativeDateChange,
    handleMaskedInputChange,
    handleFocus,
    handleBlur,
    handleKeyDown,
  };
};
